// @flow

import { observer } from "mobx-react";
import React from "react";
import ResizeObserver from 'react-resize-observer';
import Hotkeys from 'react-hot-keys';

import Dispatcher from "app/util/Dispatcher";

import TimelinelyPlayer, { states } from "timelinely-player/TimelinelyPlayer";
import type { VideoSources } from "timelinely-player/TimelinelyPlayer";
import PlayButton from "app/components/PlayButton/PlayButton";
import VolumeButton from "app/components/VolumeButton/VolumeButton";
import "./VideoPlayer.scss";
import VideoPlayerStore from "app/stores/VideoPlayerStore";
import AppearanceStore from "app/stores/AppearanceStore";
import TimelineItemStore from "../../stores/TimelineItemStore";

const updateProgressInterval = 200;

const heightRatio = 0.5625;

type Props = {
    source: string,
    originalId: ?string,
    videoUrls: ?VideoSources,
    thumbnail: string,
    volume?: number,
    onPlay?: Function,
    onPause?: Function,
    onVolumeChange?: Function,    
    autoPlay?: boolean,
    onStateChange?: Function,
    onPlay?: Function,
    onEnded: ?Function,
    onProgress?: Function,
    stateDispatcher?: Dispatcher,
    seekDispatcher?: Dispatcher,
    customControls?: Boolean,
    isMainVideo?: Boolean,
};

type State = {
    started: boolean,
};

@observer
export default class VideoPlayer extends React.Component<Props, State> {
    timelinelyPlayer: TimelinelyPlayer;

    iframe: ?HTMLIFrameElement;
    container: ?HTMLElement;
    video: ?HTMLElement;
    wrapper: ?HTMLElement;

    constructor(props: Props) {
        super(props);
        this.state = {
            started: false,
        };
    }

    handleUpdateState({ state }: any): void {
        if (this.props.onStateChange) this.props.onStateChange(state);

        setTimeout(() => {
            this.setState({ state, started: true });
        }, 50);

        if (state === states.PLAYING && this.props.onPlay) {
            this.props.onPlay();
        }
        if(state === states.ENDED && this.props.onEnded) {
            this.props.onEnded();
        }
    }

    handleUpdateProgress() {
        if (this.state.state !== states.PLAYING) return;

        const progressFn = this.props.onProgress;
        if (!progressFn)
            throw Error("onProgress function must not be null/undefined");

        const bufferedPercentagePromise = this.timelinelyPlayer
            .bufferedPercentage;
        const currentTimePromise = this.timelinelyPlayer.currentTime;

        Promise.all([bufferedPercentagePromise, currentTimePromise]).then(
            ([bufferedPercentage, currentTime]) => {
                progressFn(currentTime, bufferedPercentage);
            }
        );
    }

    resizeIframe = () => {
        const container = this.container;
        const iframe = this.iframe;

        if (!container || !iframe) return;

        let width = container.offsetWidth;

        iframe.style.width = `${width}px`;
        iframe.removeAttribute("width");
        
        if (!this.props.isMainVideo) {
            const height = width > 900 ? width/16*6 : width/16*9
            iframe.style.height = `${height}px`;
            iframe.removeAttribute("height");
            container.style.height = `${height}px`;
            container.removeAttribute("height");
        }
    };

    setPlayerState = (state: string) => {
        if (state === states.PLAYING) this.timelinelyPlayer.play();
        else this.timelinelyPlayer.pause();
    };

    seekTo = (time: number) => {
        this.timelinelyPlayer.seekTo(time);
    };

    setVolume() {
        if (typeof this.props.volume !== "undefined") {
            this.timelinelyPlayer.volume = this.props.volume;
        }
    }

    componentDidMount() {
        const container = this.container;
        const wrapper = this.wrapper;
        const video = this.video;

        if (!container || !wrapper || !video || !document.body) return;
        
        wrapper.style.height = `${wrapper.clientWidth * heightRatio}px`;
        setTimeout(() => {
            wrapper.style.height = `${wrapper.clientWidth * heightRatio}px`;
        }, 0);

        this.timelinelyPlayer = TimelinelyPlayer.bySource(
            this.props.source,
            this.props.originalId,
            this.props.videoUrls,
            video,
            true
        );
        this.setVolume();

        this.timelinelyPlayer.iframe.then((iframe: ?HTMLIFrameElement) => {
            this.iframe = iframe;
            this.resizeIframe();

            container.className += " VideoPlayer--loaded";
            if (iframe) {
                iframe.className = "animated fadeIn";
            }
            wrapper.style.height = "auto";

            if (this.props.onProgress)
                setInterval(
                    this.handleUpdateProgress.bind(this),
                    updateProgressInterval
                );
        });

        this.timelinelyPlayer.onChangeState(this.handleUpdateState.bind(this));
        if (this.props.stateDispatcher)
            this.props.stateDispatcher.listen(this.setPlayerState);
        if (this.props.seekDispatcher)
            this.props.seekDispatcher.listen(this.seekTo);
        if (this.props.autoPlay === true) this.timelinelyPlayer.play();
    }

    componentDidUpdate() {
        this.setVolume();
    }

    componentWillUnmount() {
        if (this.props.stateDispatcher)
            this.props.stateDispatcher.unlisten(this.setPlayerState);
        if (this.props.seekDispatcher)
            this.props.seekDispatcher.unlisten(this.seekTo);
        this.timelinelyPlayer.cleanup();
    }

    renderMobilePaused() {
        if (!this.state.started && VideoPlayerStore.state === states.PAUSED) return null;
        if (!AppearanceStore.isMobileBrowser) return null;
        if (VideoPlayerStore.state !== states.PAUSED) return null;

        return (
            <div className="VideoPlayer__customControls">
                <PlayButton state={states.PAUSED} onPlay={this.props.onPlay} onPause={this.props.onPause}/>
            </div>
        )
    }

    renderCustomControls() {
        if (!this.state.started || !this.props.customControls) return null;
    
        if (typeof this.props.volume === 'undefined' || !this.props.onPlay || !this.props.onPause || !this.props.onVolumeChange) {
            throw new Error('Missing video player props for custom controls');
        }

        return (
            <div className="VideoPlayer__customControls">
                <PlayButton state={this.state.state} onPlay={this.props.onPlay} onPause={this.props.onPause}/>
                <VolumeButton volume={this.props.volume} onChange={this.props.onVolumeChange}/>
            </div>
        );
    }

    toggleOnPlay(e) {
        VideoPlayerStore.setSeeking(false);
        let newstate = states.PAUSED;
        if (this.state.state === states.PAUSED) {
            newstate = states.PLAYING;
        }
        this.setPlayerState(newstate);
        this.setVolume();        
    };

    renderMobilePlayToggle() {
        if (!this.props.isMainVideo) return null;

        return (
            <div className="VideoBeforePlayOverlay" onClick={this.toggleOnPlay.bind(this)} >
                <div className="VideoBeforePlayOverlay__background" />
            </div>
        );
    }

    onKeyUp(keyName, e, handle) {

        let offset = 0;
        switch (keyName) {
            case 'left':
                offset = -1
                break
            case 'right':
                offset = 1;
                break;
            default:
                return null;
        }

        TimelineItemStore.unExpand();
        VideoPlayerStore.setPreventAutoPlay(true);
        VideoPlayerStore.seekTo(VideoPlayerStore.progress + offset);
    }

    render() {
        return (
            <div
                ref={container => (this.container = container)}
                className="VideoPlayer animated fadeIn"
            >
                <ResizeObserver onResize={this.resizeIframe} />
                <div ref={wrapper => (this.wrapper = wrapper)} className="VideoPlayer__wrapper">
                    { 
                        this.props.isMainVideo
                        ? (
                            <Hotkeys 
                                keyName="right,left" 
                                onKeyUp={this.onKeyUp.bind(this)}
                                on
                            >
                                <div className="VideoPlayer__cropping">
                                    <div className="VideoPlayer__to_crop">
                                        <div className="VideoPlayer__player_wrapper">
                                            <div id="player" ref={video => (this.video = video)} />
                                        </div>
                                    </div>
                                </div>
                            </Hotkeys>
                        )
                        : (
                            <div className="VideoPlayer__player" ref={video => (this.video = video)} />
                        )
                    }
                    {this.renderMobilePlayToggle()}
                </div>
                {this.renderCustomControls()}
                {this.renderMobilePaused()}
            </div>
        );
    }
}



// WEBPACK FOOTER //
// ./src/app/components/VideoPlayer/VideoPlayer.js